import React, { useState } from 'react';
import { FlowType } from '../AddNewController';
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import CustomerCareSelect from '../../../Shared/CustomerCareSelect';
import { ReactComponent as AssignUser } from '../../../../images/icons/assign_user.svg';
import {
  darkPurple,
  dimGrey,
  lightBlack,
  lightGrey,
} from '../../../../mainTheme';

interface FlowTypeSelectionProps {
  onSelect: (type: FlowType) => void;
  onCancel: () => void;
  isFacilityAdmin: boolean;
}

const TYPOGRAPHY_STYLES = {
  heading: {
    fontWeight: 500,
    fontSize: '22px',
    lineHeight: '30px',
    letterSpacing: '0.36px',
    color: darkPurple,
  },
  subheading: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.2px',
    color: dimGrey,
  },
  menuItemLabel: {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.2px',
    color: lightBlack,
  },
  menuItemDescription: {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: 0,
    color: lightGrey,
  },
} as const;

const OPTIONS = [
  {
    value: 'facility',
    label: 'Facility',
    description: 'An organization that cares for a group of patients.',
  },
  {
    value: 'facility_admin',
    label: 'Facility admin',
    description: 'An individual that runs operations of a facility.',
  },
  {
    value: 'patient',
    label: 'Patient',
    description: 'An individual that uses the Hub.',
  },
] as const;

export const FlowTypeSelection: React.FC<FlowTypeSelectionProps> = ({
  onSelect,
  onCancel,
  isFacilityAdmin,
}) => {
  const [selectedType, setSelectedType] = useState<FlowType | ''>('');

  const handleChange = (e: SelectChangeEvent<string>) => {
    setSelectedType(e.target.value as FlowType);
  };

  const handleNext = () => selectedType && onSelect(selectedType);

  const renderMenuItem = (option: {
    value: string;
    label: string;
    description: string;
  }) => (
    <MenuItem key={option.value} value={option.value}>
      <Box>
        <Typography sx={TYPOGRAPHY_STYLES.menuItemLabel}>
          {option.label}
        </Typography>
        <Typography sx={TYPOGRAPHY_STYLES.menuItemDescription}>
          {option.description}
        </Typography>
      </Box>
    </MenuItem>
  );

  // this will Filter options based on role
  const filteredOptions = isFacilityAdmin
    ? OPTIONS.filter((opt) => opt.value !== 'facility').map((opt) =>
        opt.value === 'facility_admin'
          ? {
              ...opt,
              value: 'location_admin',
              label: 'Location admin',
              description: 'An individual that runs operations of a location.',
            }
          : opt,
      )
    : OPTIONS;

  return (
    <Box display="flex" flexDirection="column" gap="24px">
      <AssignUser />
      <Box display="flex" flexDirection="column" gap="8px">
        <Typography sx={TYPOGRAPHY_STYLES.heading}>Add new</Typography>
        <Typography sx={TYPOGRAPHY_STYLES.subheading}>
          Select what you would like to add.
        </Typography>
      </Box>

      <FormControl fullWidth>
        <CustomerCareSelect
          label="Account type"
          onChange={handleChange}
          fullWidth
          value={selectedType}
          renderSelectedValue={(value) => {
            const option = filteredOptions.find((opt) => opt.value === value);
            return option?.label ?? '';
          }}
        >
          {filteredOptions.map(renderMenuItem)}
        </CustomerCareSelect>
      </FormControl>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', my: '8px' }}>
        <Button
          variant="text"
          color="secondary"
          onClick={onCancel}
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
            width: '180px',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleNext}
          disabled={!selectedType}
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
            width: '180px',
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};
